import {z} from 'zod'

const rentalBillingCadenceSchema = z.enum(['hourly', 'daily', 'weekly', 'monthly'])
const rentalBillingDateSchema = z.union([z.date(), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)])

const rentalBillingPreviewStintBaseRawSchema = z.object({
    rental_stint_id: z.string(),
    rental_asset_display_name: z.string(),
    previous_quantity: z.number().int().nonnegative(),
    new_quantity: z.number().int().nonnegative(),
    covered_start: z.string(),
    covered_end: z.string(),
    total_price: z.coerce.number(),
})

const rentalBillingPreviewStintRawSchema = z.discriminatedUnion('is_uniquely_identified', [
    rentalBillingPreviewStintBaseRawSchema.extend({is_uniquely_identified: z.literal(false)}),
    rentalBillingPreviewStintBaseRawSchema.extend({
        is_uniquely_identified: z.literal(true),
        rental_asset_id: z.string(),
    }),
])

const rentalBillingPreviewGroupRawSchema = z.object({
    rental_stint_ids: z.array(z.string()),
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    billing_type: rentalBillingCadenceSchema,
    billing_rate: z.coerce.number(),
    unit: z.enum(['hour', 'day', 'week', 'month']),
    previous_quantity: z.number().int().nonnegative(),
    new_quantity: z.number().int().nonnegative(),
    covered_start: z.string(),
    covered_end: z.string(),
    total_price: z.coerce.number(),
    stints: z.array(rentalBillingPreviewStintRawSchema),
    job_id: z.string().nullable().optional(),
    job_name: z.string().nullable().optional(),
})

export const rentalBillingPreviewRawSchema = z.object({
    groups: z.array(rentalBillingPreviewGroupRawSchema),
    excluded_stint_count: z.number().int().nonnegative(),
    fully_allocated_stint_count: z.number().int().nonnegative(),
})

export const rentalBillingPreviewSchema = rentalBillingPreviewRawSchema.transform((raw) => ({
    groups: raw.groups.map((group) => ({
        rentalStintIds: group.rental_stint_ids,
        rentalAssetTypeId: group.rental_asset_type_id,
        rentalAssetTypeName: group.rental_asset_type_name,
        billingType: group.billing_type,
        billingRate: group.billing_rate,
        unit: group.unit,
        previousQuantity: group.previous_quantity,
        newQuantity: group.new_quantity,
        coveredStart: group.covered_start,
        coveredEnd: group.covered_end,
        totalPrice: group.total_price,
        jobId: group.job_id ?? null,
        jobName: group.job_name ?? null,
        stints: group.stints.map((stint) => ({
            rentalStintId: stint.rental_stint_id,
            ...(stint.is_uniquely_identified ? {rentalAssetId: stint.rental_asset_id} : {}),
            rentalAssetDisplayName: stint.rental_asset_display_name,
            isUniquelyIdentified: stint.is_uniquely_identified,
            previousQuantity: stint.previous_quantity,
            newQuantity: stint.new_quantity,
            coveredStart: stint.covered_start,
            coveredEnd: stint.covered_end,
            totalPrice: stint.total_price,
        })),
    })),
    excludedStintCount: raw.excluded_stint_count,
    fullyAllocatedStintCount: raw.fully_allocated_stint_count,
}))

const rentalBillingChargeLineItemRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    quantity: z.coerce.number(),
    unit: z.string(),
    unit_price: z.coerce.number(),
    total_price: z.coerce.number(),
})

export const rentalBillingChargesResponseSchema = z
    .object({billable_line_items: z.array(rentalBillingChargeLineItemRawSchema)})
    .transform((raw) => ({
        billableLineItems: raw.billable_line_items.map((lineItem) => ({
            id: lineItem.id,
            name: lineItem.name,
            description: lineItem.description,
            quantity: lineItem.quantity,
            unit: lineItem.unit,
            unitPrice: lineItem.unit_price,
            totalPrice: lineItem.total_price,
        })),
    }))

export const rentalBillingChargeCreateInputSchema = z.object({
    billThrough: rentalBillingDateSchema,
    rentalStintIds: z.array(z.string()).min(1),
})

export type RentalBillingPreview = z.infer<typeof rentalBillingPreviewSchema>
export type RentalBillingChargeCreateInput = z.infer<typeof rentalBillingChargeCreateInputSchema>
export type RentalBillingChargesResponse = z.infer<typeof rentalBillingChargesResponseSchema>
